import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Check, CalendarDays } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const services = [
  "Nettoyage & désinfection de climatisation",
  "Nettoyage de panneaux solaires",
  "Nettoyage de gouttières",
  "Ramonage",
  "Tous travaux",
];

const slots = [
  { id: "matin", label: "Matin", hint: "8h — 12h" },
  { id: "apres-midi", label: "Après-midi", hint: "13h — 19h" },
];

const today = new Date().toISOString().split("T")[0];

export const Booking = () => {
  const [service, setService] = useState(null);
  const [slot, setSlot] = useState(null);
  const [form, setForm] = useState({ date: "", name: "", phone: "", address: "", city: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!service || !slot) {
      setError("Choisissez une prestation et un moment de la journée.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/appointments`, { ...form, service, slot });
      setSent(true);
    } catch {
      setError("Une erreur est survenue. Appelez-nous au 07 60 40 53 73.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="rendez-vous"
      className="px-6 md:px-12 py-32 md:py-48 border-t border-white/10"
      data-testid="booking-section"
    >
      <div className="grid md:grid-cols-2 gap-16 md:gap-32">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
        >
          <p className="text-xs uppercase tracking-[0.3em] text-gold mb-8">Rendez-vous</p>
          <h2
            className="font-serif text-5xl md:text-7xl tracking-tighter leading-[0.95]"
            data-testid="booking-title"
          >
            réservez votre<br />
            <span className="italic font-light text-gold">intervention.</span>
          </h2>
          <p className="text-sm md:text-base text-ash font-light leading-relaxed mt-10 max-w-md">
            Choisissez votre prestation, un jour et un moment. Nous vous
            rappelons pour confirmer le rendez-vous et établir le devis.
          </p>
          <div className="mt-10 flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-bone">
            <CalendarDays className="w-4 h-4 text-gold" />
            Du lundi au vendredi — 8h à 19h
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, delay: 0.15, ease: [0.76, 0, 0.24, 1] }}
        >
          {sent ? (
            <div className="h-full flex flex-col justify-center" data-testid="booking-success">
              <p className="font-serif text-4xl md:text-5xl italic text-gold mb-6">Demande envoyée.</p>
              <p className="text-sm text-ash font-light max-w-sm">
                Merci {form.name}. Nous vous appelons au {form.phone} pour
                confirmer votre rendez-vous — vous recevrez ensuite un SMS de
                confirmation.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-12" data-testid="booking-form">
              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-ash mb-5">1 — Choix de la prestation</p>
                <div>
                  {services.map((s, i) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setService(s)}
                      data-testid={`booking-service-0${i + 1}`}
                      className={`w-full flex items-center justify-between py-4 border-b border-white/10 text-left font-serif text-xl md:text-2xl tracking-tight transition-colors duration-500 ${
                        service === s ? "text-gold" : "text-bone hover:text-gold"
                      }`}
                    >
                      {s}
                      {service === s && <Check className="w-5 h-5 shrink-0" />}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-ash mb-5">2 — Jour & moment</p>
                <input
                  type="date"
                  required
                  min={today}
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                  data-testid="booking-date-input"
                  style={{ colorScheme: "dark" }}
                  className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-bone font-light transition-colors duration-500 rounded-none mb-6"
                />
                <div className="grid grid-cols-2 gap-4">
                  {slots.map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setSlot(s.id)}
                      data-testid={`booking-slot-${s.id}`}
                      className={`border py-4 text-xs uppercase tracking-[0.2em] font-medium transition-colors duration-500 ${
                        slot === s.id
                          ? "border-gold bg-gold text-obsidian"
                          : "border-white/20 text-ash hover:border-gold hover:text-bone"
                      }`}
                    >
                      {s.label}
                      <span className="block mt-1 text-[10px] opacity-70">{s.hint}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <p className="text-xs uppercase tracking-[0.25em] text-ash mb-5">3 — Vos coordonnées</p>
                <div className="space-y-8">
                  <div className="grid md:grid-cols-2 gap-8">
                    <input
                      type="text" required placeholder="Votre nom"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      data-testid="booking-name-input"
                      className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-bone font-light placeholder:text-ash/60 transition-colors duration-500 rounded-none"
                    />
                    <input
                      type="tel" required placeholder="Votre téléphone"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      data-testid="booking-phone-input"
                      className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-bone font-light placeholder:text-ash/60 transition-colors duration-500 rounded-none"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-8">
                    <input
                      type="text" required placeholder="Adresse"
                      value={form.address}
                      onChange={(e) => setForm({ ...form, address: e.target.value })}
                      data-testid="booking-address-input"
                      className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-bone font-light placeholder:text-ash/60 transition-colors duration-500 rounded-none"
                    />
                    <input
                      type="text" required placeholder="Ville"
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      data-testid="booking-city-input"
                      className="w-full bg-transparent border-b border-white/20 focus:border-gold outline-none py-3 text-bone font-light placeholder:text-ash/60 transition-colors duration-500 rounded-none"
                    />
                  </div>
                </div>
              </div>

              {error && (
                <p className="text-xs text-red-400 tracking-wide" data-testid="booking-error">{error}</p>
              )}
              <button
                type="submit"
                disabled={loading}
                data-testid="booking-submit-button"
                className="group relative text-xs uppercase tracking-[0.3em] text-bone hover:text-gold transition-colors duration-500 disabled:opacity-50"
              >
                {loading ? "Envoi…" : "Demander ce rendez-vous"}
                <span className="block h-px w-full bg-white/20 mt-3 overflow-hidden">
                  <span className="block h-full w-full bg-gold origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-700 ease-out" />
                </span>
              </button>
            </form>
          )}
        </motion.div>
      </div>
    </section>
  );
};
